import {useState} from "react";
import {useCookies} from "react-cookie";
import {Mode} from "../ScheduleList/ScheduleList";
import {useSelector} from "react-redux";
import moment from "moment";
import {getWeekDayName} from "../../../services/generalService";
import {DEFAULT_DATE_FORMAT} from "../../../services/scheduleService";
import useWindowDimensions from "../../../services/hooks/useWindowDimensions";
import ScheduleView, {ViewOption} from "./ScheduleView";

const SMALL_SCREEN_WIDTH = 800;

function ScheduleFilteringView({activeView, scheduleTarget, scheduleType, currentTermLessons,
                          previousTermLessons, exams, currentPeriod}) {

    const [scheduleFiltersCookies, setScheduleFiltersCookies] = useCookies(["scheduleFilters"]);
    const [subgroupFilter, setSubgroupFilter] = useState(scheduleType === 2
        ? [true, true]
        : scheduleFiltersCookies.scheduleFilters?.subgroupFilters || [true, true]
    );
    const [passedLessonsFilter, setPassedLessonsFilter] = useState(getInitialPassedLessonsFilter());
    const [sessionFilter, setSessionFilter] = useState(getInitialSessionFilter());
    const [showTodayTomorrowFilter, setShowTodayTomorrowFilter] = useState(getInitialShowTodayTomorrowFilter());
    const currentWeekNumber = useSelector(state => state.schedule.currentWeekNumber);

    const weekFilters = useSelector(state => state.schedule.weekFilters);
    const width = useWindowDimensions(SMALL_SCREEN_WIDTH);

    const scheduleView = (width <= SMALL_SCREEN_WIDTH) ? ViewOption.Mobile : activeView;

    const filters = [
        {
            shouldApply: () => true,
            filter: (lesson) => lesson.numSubgroup === 0 || subgroupFilter[lesson.numSubgroup - 1]
        },
        {
            shouldApply: (mode) => mode !== Mode.todayAndTomorrow,
            filter: (lesson) => lesson.weekNumber.some(w => weekFilters[w - 1])
        },
        {
            shouldApply: (mode, isCurrentPeriod) => mode !== Mode.todayAndTomorrow && isCurrentPeriod,
            filter: (lesson) => passedLessonsFilter || !lessonHasPassed(lesson)
        },
        {
            shouldApply: (mode) => mode !== Mode.todayAndTomorrow,
            filter: (lesson) => sessionFilter || !lesson.dateLesson
        }
    ];

    const filteredCurrentLessons = getFilteredTermLessons(currentTermLessons,
        currentTermLessons?.term === currentPeriod);

    const filteredPreviousLessons = getFilteredTermLessons(previousTermLessons,
        previousTermLessons?.term === currentPeriod);

    const todayTomorrowLessons = getFilteredTodayTomorrowLessons();

    function hasSchedulesBySubgroups(lessons) {
        const objectValues = Object.values(lessons);
        let hasLessonsBySubgroups = false;
        for (const lessonsForWeekday of objectValues) {

            hasLessonsBySubgroups = lessonsForWeekday.some(l => {
                return l.numSubgroup === 1 || l.numSubgroup === 2;
            });
            if (hasLessonsBySubgroups) {
                break;
            }
        }
        return hasLessonsBySubgroups;
    }

    function getFilteredTodayTomorrowLessons() {
        if (previousTermLessons || currentTermLessons) {
            return getTodayTomorrowLessonsFromLessons(previousTermLessons ? previousTermLessons.schedules
                : currentTermLessons.schedules);
        }
        if (exams) {
            return getTodayTomorrowLessonsFromExams(exams);
        }
        return null;
    }

    function getInitialShowTodayTomorrowFilter() {
        if (!scheduleFiltersCookies.scheduleFilters) return false;
        return scheduleFiltersCookies.scheduleFilters.showTodayTomorrowFilter;
    }

    function getInitialPassedLessonsFilter() {
        if (!scheduleFiltersCookies.scheduleFilters) return false;
        return scheduleFiltersCookies.scheduleFilters.passedLessonsFilter;
    }

    function getInitialSessionFilter() {
        if (!scheduleFiltersCookies.scheduleFilters) return true;
        return scheduleFiltersCookies.scheduleFilters.sessionFilter;
    }

    const setFiltersToCookies = (value, typeFilter) => {
        const expirationDate = new Date();
        expirationDate.setMonth(expirationDate.getMonth() + 1);

        let subgrFilters = subgroupFilter;
        let passFilter = passedLessonsFilter;
        let sessFilter = sessionFilter;
        let showClosestDaysFilter = showTodayTomorrowFilter;

        if (typeFilter === "subgroupFilters") {
            setSubgroupFilter(value);
            subgrFilters = value;
        } else if (typeFilter === "passedLessonsFilter") {
            setPassedLessonsFilter(value);
            passFilter = value;
        } else if (typeFilter === "sessionFilter") {
            setSessionFilter(value);
            sessFilter = value;
        } else if (typeFilter === "showTodayAndTomorrowFilter") {
            setShowTodayTomorrowFilter(value);
            showClosestDaysFilter = value;
        }

        const scheduleFilters = {
            subgroupFilters: subgrFilters,
            passedLessonsFilter: passFilter,
            sessionFilter: sessFilter,
            showTodayTomorrowFilter: showClosestDaysFilter
        };
        setScheduleFiltersCookies("scheduleFilters", JSON.stringify(scheduleFilters), {
            path: "/",
            expires: expirationDate
        });
    }

    function lessonHasPassed(lesson) {
        let lastLessonDate = null;
        if (lesson.endLessonDate) {
            lastLessonDate = lesson.endLessonDate;
        } else if (lesson.dateLesson) {
            lastLessonDate = lesson.dateLesson;
        }
        if (!lastLessonDate) return false;

        const today = moment().startOf('day');
        return today.isAfter(moment(lastLessonDate, DEFAULT_DATE_FORMAT).startOf('day'));
    }

    function getTodayTomorrowLessonsFromLessons(lessons) {
        if (!lessons) return null;
        const today = moment();
        const lessonsForTodayTomorrow = {};
        let currentDay;
        for (let i = 0; i < 2; i++) {
            currentDay = moment(today).add(i, 'days');

            const weekDayName = getWeekDayName(currentDay.toDate());
            const lessonsForDay = lessons[weekDayName];
            if (lessonsForDay) {
                lessonsForTodayTomorrow[weekDayName] = lessonsForDay.filter(isLessonTodayOrTomorrow);
            }
        }
        return getFilteredLessons(lessonsForTodayTomorrow, Mode.todayAndTomorrow);
    }

    function getTodayTomorrowLessonsFromExams(exams) {
        if (!exams) return null;
        const today = moment();
        const lessonsForTodayTomorrow = {};
        let currentDay;
        for (let i = 0; i < 2; i++) {
            currentDay = moment(today).add(i, 'days').startOf('day');
            const currentDayAsStr = currentDay.format(DEFAULT_DATE_FORMAT);
            const dayLessons = exams.filter(exam => {
                if (exam.dateLesson) {
                    return exam.dateLesson === currentDayAsStr;
                }
                if (exam.announcement) {
                    const firstLessonDate = moment(exam.startLessonDate, DEFAULT_DATE_FORMAT);
                    const lastLessonDate = moment(exam.endLessonDate, DEFAULT_DATE_FORMAT);
                    return today.isBetween(firstLessonDate, lastLessonDate, 'days', '[]');
                }
                return false;
            });

            const weekDayName = getWeekDayName(currentDay.toDate());
            lessonsForTodayTomorrow[weekDayName] = dayLessons;
        }
        return getFilteredLessons(lessonsForTodayTomorrow, Mode.todayAndTomorrow);
    }

    function getFilteredLessons(lessons, mode, isCurrentPeriod) {
        const lessonsArray = Object.entries(lessons);
        const filteredLessons = lessonsArray.map(([day, dayLessons]) => {
            const filteredDayLessons = dayLessons.filter(l => applyFiltersToLesson(l, mode, isCurrentPeriod));
            return [day, filteredDayLessons];
        });
        return Object.fromEntries(filteredLessons);
    }

    function isLessonTodayOrTomorrow(lesson) {
        const today = moment();
        if (lesson.dateLesson) {
            const date = moment(lesson.dateLesson, DEFAULT_DATE_FORMAT);
            const datesDiff = date.diff(moment(), 'days');
            return datesDiff === 0 || datesDiff === 1;
        }

        if (lesson.announcement || lesson.weekNumber.some(w => {
            return w === (today.isoWeekday() === 7 ? (currentWeekNumber + 1) % 4 : currentWeekNumber)
        })) {
            const firstLessonDate = moment(lesson.startLessonDate, DEFAULT_DATE_FORMAT);
            const lastLessonDate = moment(lesson.endLessonDate, DEFAULT_DATE_FORMAT);
            const tomorrow = moment().add(1, 'days');
            return today.isBetween(firstLessonDate, lastLessonDate, 'days', '[]')
                || tomorrow.isBetween(firstLessonDate, lastLessonDate, 'days', '[]');
        }
        return false;
    }

    function getFilteredTermLessons(termLessons, isCurrentPeriod) {
        if (!termLessons) return null;
        const filteredLessons = getFilteredLessons(termLessons.schedules, Mode.regularLessons, isCurrentPeriod);
        return {
            ...termLessons,
            schedules: filteredLessons,
            hasSchedulesBySubgroups: hasSchedulesBySubgroups(termLessons.schedules)
        };
    }

    function applyFiltersToLesson(lesson, mode, isCurrentPeriod) {
        if (lesson.announcement) return true;
        let passed = true;
        for (const f of filters) {
            if (f.shouldApply(mode, isCurrentPeriod)) {
                passed = f.filter(lesson);
                if (!passed) break;
            }
        }
        return passed;
    }

    const propsToPass = {
        viewMode: scheduleView,
        scheduleType,
        scheduleTarget,
        currentTermLessons: filteredCurrentLessons,
        previousTermLessons: filteredPreviousLessons,
        exams: exams,
        todayTomorrowLessons,
        currentPeriod,
        updateFilter: setFiltersToCookies,
        filters: {
            subgroupFilter,
            passedLessonsFilter,
            sessionFilter,
            showTodayTomorrowFilter
        }
    }

    return (<ScheduleView {...propsToPass} key={scheduleView} />);
}

export default ScheduleFilteringView;